import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Search } from 'lucide-react';
import { STATUS_META, formatInr, formatShortDate, formatSignedInr, formatSignedPercent } from '../../utils/gmpBoard';
import { DEMAND_BANDS, TOP_TABS, demandBarWidth, demandTone, formatTimes } from '../../utils/topSubscribed';

const COLUMN_COUNT = 8;
const FIELD_CLASS =
  'px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]';

const Dash = () => <span className="text-slate-300">—</span>;

// rows: utils/topSubscribed.js mapTopSubscribedRows, already filtered
export const TopSubscribedTable = forwardRef(
  ({ rows, maxTimes, tab, onTabChange, tabCounts, band, onBandChange, query, onQueryChange, loading, emptyMessage }, ref) => (
    <div ref={ref} className="scroll-mt-20 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Board" className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80">
          {TOP_TABS.map((option) => {
            const active = option.key === tab;
            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => onTabChange(option.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                  active ? 'bg-[#008080] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {option.label} ({tabCounts[option.key] || 0})
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="relative block">
            <span className="sr-only">Search issues</span>
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search company..."
              className={`${FIELD_CLASS} pl-8 w-44 sm:w-56`}
            />
          </label>
          <label className="block">
            <span className="sr-only">Demand band</span>
            <select value={band} onChange={(e) => onBandChange(e.target.value)} className={`${FIELD_CLASS} font-semibold`}>
              {DEMAND_BANDS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <section aria-label="Most subscribed IPOs" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
        <div className="overflow-x-auto scrollbar-slim">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th scope="col" className="py-3 pl-4 sm:pl-5 pr-2 w-12">#</th>
                <th scope="col" className="py-3 px-2 min-w-[200px]">Company</th>
                <th scope="col" className="py-3 px-3 whitespace-nowrap">Status</th>
                <th scope="col" className="py-3 px-3 whitespace-nowrap">Bidding Window</th>
                <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">Price</th>
                <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">Issue Size</th>
                <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">GMP</th>
                <th scope="col" className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">Times Subscribed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
              {loading &&
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="animate-pulse">
                    <td className="py-3 pl-4 sm:pl-5 pr-2"><div className="w-5 h-4 rounded bg-slate-100" /></td>
                    <td className="py-3 px-2">
                      <div className="w-40 h-4 rounded bg-slate-200" />
                      <div className="mt-1.5 w-24 h-3 rounded bg-slate-100" />
                    </td>
                    {Array.from({ length: COLUMN_COUNT - 2 }).map((__, col) => (
                      <td key={col} className="py-3 px-3"><div className="ml-auto w-14 h-4 rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-slate-500">
                    {emptyMessage}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => {
                  const tone = demandTone(row.times);
                  const status = STATUS_META[row.status] || STATUS_META.other;
                  return (
                    <tr key={row.id} className="group hover:bg-slate-50/70 transition-colors">
                      <td className="py-3 pl-4 sm:pl-5 pr-2">
                        <span
                          className={`inline-flex w-6 h-6 rounded-md text-[11px] font-extrabold items-center justify-center ${
                            row.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {row.rank}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {row.link ? (
                            <Link to={row.link} className="font-bold text-slate-900 hover:text-[#008080] truncate">
                              {row.name}
                            </Link>
                          ) : (
                            <span className="font-bold text-slate-900 truncate">{row.name}</span>
                          )}
                          {row.fireRating >= 4 && <Flame className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" aria-hidden="true" />}
                        </div>
                        <p className="mt-0.5 text-[11px] text-slate-400 truncate">
                          {[row.board, row.exchange, row.lotSize ? `Lot ${row.lotSize.toLocaleString('en-IN')}` : null].filter(Boolean).join(' • ')}
                        </p>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold ${status.className}`}>
                          {row.statusLabel || status.label}
                        </span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap text-slate-600">
                        {row.openOn && row.closeOn ? `${formatShortDate(row.openOn)} – ${formatShortDate(row.closeOn)}` : <Dash />}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                        {row.price ? formatInr(row.price) : <Dash />}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                        {row.sizeCr ? `₹${row.sizeCr.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr` : <Dash />}
                      </td>
                      <td className="py-3 px-3 text-right whitespace-nowrap tabular-nums">
                        {row.hasGmp ? (
                          <>
                            <span className={row.gmp >= 0 ? 'font-bold text-emerald-600' : 'font-bold text-rose-600'}>{formatSignedInr(row.gmp)}</span>
                            {row.gmpPercent !== null && (
                              <span className="block text-[10px] text-slate-400">{formatSignedPercent(row.gmpPercent, 1)}</span>
                            )}
                          </>
                        ) : (
                          <Dash />
                        )}
                      </td>
                      <td className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-extrabold tabular-nums ${tone.badge}`}>
                          {formatTimes(row.times)}
                        </span>
                        <span className="mt-1 block w-20 ml-auto h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                          <span className={`block h-full rounded-full ${tone.bar}`} style={{ width: `${demandBarWidth(row.times, maxTimes)}%` }} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 && (
          <p className="px-4 sm:px-5 py-3 border-t border-slate-100 text-[11px] text-slate-400">
            Ranked by times subscribed across the public book. Anchor allotments are excluded, and figures for open issues
            keep moving until bidding closes.
          </p>
        )}
      </section>
    </div>
  )
);

TopSubscribedTable.displayName = 'TopSubscribedTable';

export default TopSubscribedTable;
